import {
	Alert,
	Badge,
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
} from "@sembark-travel/ui-base"
import {
	Form,
	GetFieldValue,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextAreaInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { useXHR } from "@sembark-travel/xhr"
import { useAuthUser } from "../Auth"
import { usePrivateChannel } from "../Websockets"
import { useTripWhatsApp, type TTripWhatsAppOverview } from "./store"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { trackWhatsAppMessageSent } from "@sembark-travel/tracking"
import * as Validator from "yup"
import { FormApi } from "final-form"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
	SelectWATemplate,
	TWATemplate,
	useFetchWATemplateParameters,
} from "../Integrations"
import type { TWAQuickSendResult } from "../WhatsAppQuickSends"
import {
	type TWhatsAppMessage,
	TWhatsAppThread,
	type TWhatsAppThreadActivityPayload,
	CUSTOMER_SERVICE_WINDOW_HOURS,
	isOutsideCustomerServiceWindow,
	SendQuickSendDialog,
	useCanSendOnWhatsApp,
	useMarkThreadRead,
	useThreadMessages,
	WhatsAppConversationHeader,
	WhatsAppFollowToggle,
	WhatsAppMessageComposer,
	WhatsAppMessageList,
} from "../WhatsAppMessages"

type TSendTripWhatsAppValues = {
	wa_template?: TWATemplate
	wa_phone_number_id?: string
	template_parameters?: Record<string, string>
	custom_message?: string
}

const validateSendTripWhatsApp = Validator.object().shape({
	wa_phone_number_id: Validator.string().required(
		"Please choose a WhatsApp phone number"
	),
	wa_template_id: Validator.string().optional(),
	custom_message: Validator.string().optional(),
})

function getTemplateParameterNames(template?: TWATemplate | null): string[] {
	if (!template?.components?.length) {
		return []
	}

	return Array.from(
		new Set(
			template.components.flatMap(
				(component) =>
					component.example?.body_text_named_params?.map(
						(parameter) => parameter.param_name
					) ?? []
			)
		)
	)
}

/**
 * Meta's cap on a document caption. Past it the backend sends the message
 * separately rather than failing the whole media send.
 */
const WHATSAPP_CAPTION_MAX_LENGTH = 1024

export function TripWhatsAppCard({ tripId }: { tripId: number }) {
	const canSendOnWhatsApp = useCanSendOnWhatsApp()
	const { overview, error, isLoading, refresh, isRefreshing } = useTripWhatsApp(
		canSendOnWhatsApp ? tripId : null
	)
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const { user } = useAuthUser()

	// The open tab keeps its own `wa.thread.{id}` subscription through
	// useThreadMessages, but the others are unmounted and cannot. One tenant
	// subscription here — the same channel the inbox list uses — refreshes the
	// overview so their unread badges move too.
	const threadIds = useMemo(
		() => (overview?.threads || []).map((thread) => thread.id),
		[overview]
	)
	usePrivateChannel<TWhatsAppThreadActivityPayload>(
		user?.tenant_id ? `wa.tenant.${user.tenant_id}` : null,
		".wa.thread.activity",
		(payload) => {
			// The channel carries the whole tenant; this trip owns a handful of
			// its threads.
			if (payload?.thread_id && threadIds.includes(payload.thread_id)) {
				refresh()
			}
		}
	)

	// Nothing to show a tenant without the integration — and skipping the
	// fetch keeps it off every trip page they open.
	if (!canSendOnWhatsApp) {
		return null
	}

	return (
		<>
			<Stack borderWidth="1" rounded="md" bgColor="default">
				<Box
					paddingX="4"
					paddingY="2"
					bgColor="subtle"
					roundedTop="md"
					borderBottomWidth="1"
				>
					<Inline alignItems="center" gap="1">
						<Stack gap="px">
							<Heading fontSize="md">WhatsApp</Heading>
							{!overview?.threads?.length ? (
								overview?.contact?.phone_number_plain ? (
									<Text fontSize="sm" color="muted">
										{overview.contact.name || "Customer"} ·{" "}
										{overview.contact.phone_number_plain}
									</Text>
								) : (
									<Text fontSize="sm" color="muted">
										No customer phone number available yet
									</Text>
								)
							) : null}
						</Stack>
						{isRefreshing ? (
							<Inline alignItems="center">
								<Icons.Refresh spin={isRefreshing} opacity="20" />
							</Inline>
						) : null}
					</Inline>
				</Box>
				<Box>
					{isLoading ? (
						<Spinner alignCenter padding="4" />
					) : error ? (
						<Alert
							status="error"
							title="Unable to load WhatsApp history"
							rounded="none"
						>
							Please refresh the page and try again.
						</Alert>
					) : (
						<Stack gap="4">
							{!overview?.phone_numbers.length ? (
								<Alert
									status="info"
									title="No active WhatsApp phone numbers"
									rounded="none"
								>
									Connect an active WhatsApp sender phone number to message this
									customer from the trip page.
								</Alert>
							) : null}
							{!overview?.contact?.phone_number_plain ? (
								<Alert
									status="warning"
									title="Missing customer phone number"
									rounded="none"
								>
									Add a phone number on the primary tourist or B2B source
									contact to start a WhatsApp conversation here.
								</Alert>
							) : null}

							{overview?.threads?.length ? (
								<Tabs
									layout="col"
									defaultActive={String(overview.threads[0].id)}
								>
									{overview.threads.length > 1 ? (
										<TabsList>
											{overview.threads.map((t) => (
												<TabItem key={t.id} id={String(t.id)}>
													{t.contact?.name || t.contact?.contact_number}
													{t.unread_count ? (
														<>
															{" "}
															<Badge fullRounded primary>
																{t.unread_count}
															</Badge>
														</>
													) : null}
												</TabItem>
											))}
										</TabsList>
									) : null}
									{overview.threads.map((thread) => (
										<TabContent
											key={thread.id}
											id={thread.id.toString()}
											unmountOnInactive
										>
											<TripWhatsAppThreadCard
												tripId={tripId}
												thread={thread}
												overview={overview}
												onRefresh={refresh}
											/>
										</TabContent>
									))}
								</Tabs>
							) : (
								<Alert
									status="info"
									title="No WhatsApp conversations yet"
									rounded="none"
								>
									<Stack gap="1">
										<Text>
											Once you send a message or receive one from this customer,
											the conversation will appear here.
										</Text>
										<Box>
											<Button
												size="sm"
												onClick={openDialog}
												disabled={
													!overview?.contact?.phone_number_plain ||
													!overview?.phone_numbers?.length
												}
											>
												<Icons.WhatsApp spin={isRefreshing} /> Send WhatsApp
												Message
											</Button>
										</Box>
									</Stack>
								</Alert>
							)}
						</Stack>
					)}
				</Box>
			</Stack>
			{overview ? (
				<SendTripWhatsAppDialog
					open={isDialogOpen}
					onClose={closeDialog}
					tripId={tripId}
					overview={overview}
					onSuccess={async () => {
						await refresh()
						closeDialog()
					}}
				/>
			) : null}
		</>
	)
}

function TripWhatsAppThreadCard({
	tripId,
	thread,
	overview,
	onRefresh,
}: {
	tripId: number
	thread: TWhatsAppThread
	overview: TTripWhatsAppOverview
	onRefresh: () => Promise<TTripWhatsAppOverview | undefined>
}) {
	const xhr = useXHR()
	const [isTemplateDialogOpen, openTemplateDialog, closeTemplateDialog] =
		useDialog()
	const [isQuickSendDialogOpen, openQuickSendDialog, closeQuickSendDialog] =
		useDialog()
	const threadPhoneNumberId = thread.phone_number?.id
	const outsideServiceWindow = isOutsideCustomerServiceWindow(
		thread.last_inbound_message_at
	)

	// The trip page is not the inbox: landing on it is not the same as sitting
	// down to read. A conversation with unread messages stays behind a prompt,
	// and only opens — and only then counts as read — once the operator engages
	// with it. Engaging sticks, so later messages appear as they arrive instead
	// of hiding the thread again mid-conversation.
	const unreadCount = thread.unread_count ?? 0
	const [hasEngaged, setHasEngaged] = useState(false)
	const engage = useCallback(() => setHasEngaged(true), [])
	// Derived, not stored: a thread with nothing unread is already open, and one
	// that gains an unread message closes again — unless the operator has
	// engaged, in which case it stays open and new messages just appear.
	const isRevealed = hasEngaged || unreadCount === 0
	useMarkThreadRead(thread, onRefresh, isRevealed)
	// Paginated + live message history for this thread (shared with the inbox).
	// `onActivity` refreshes the overview (latest-message preview / ordering) when
	// a new message arrives.
	const { messages, loadOlder, hasMoreOlder, isLoadingOlder, syncNew } =
		useThreadMessages(thread.id, { onActivity: onRefresh })

	// The message the operator is replying to. Picking another replaces it.
	const [replyTo, setReplyTo] = useState<TWhatsAppMessage | null>(null)

	// The trip reply posts to the trip endpoint (it carries the trip's chosen
	// sender phone number); the shared conversation owns read/follow/messages.
	const composer = (
		<WhatsAppMessageComposer
			sealed={Boolean(thread.contact?.identity_changed_at)}
			outsideServiceWindow={outsideServiceWindow}
			disabled={!threadPhoneNumberId}
			replyTo={replyTo}
			onCancelReply={() => setReplyTo(null)}
			onSend={async ({ message, attachment, hasAttachmentField }) => {
				if (!threadPhoneNumberId) {
					throw new Error(
						"This conversation is missing its WhatsApp sender phone number."
					)
				}
				if (attachment) {
					const data = new FormData()
					data.append("wa_phone_number_id", String(threadPhoneNumberId))
					// Answers whoever is in this conversation, which is not
					// always the trip's primary contact.
					data.append("wa_thread_id", String(thread.id))
					if (message) {
						data.append("custom_message", message)
					}
					if (hasAttachmentField) {
						data.append("attachment", attachment)
					}
					if (replyTo) {
						data.append("reply_to_message_id", String(replyTo.id))
					}
					await xhr.post(`/trips/${tripId}/whatsapp`, data)
					trackWhatsAppMessageSent({
						kind: hasAttachmentField ? "attachment" : "text",
						surface: "trip",
					})
				} else {
					await xhr.post(`/trips/${tripId}/whatsapp`, {
						wa_phone_number_id: threadPhoneNumberId,
						wa_thread_id: thread.id,
						custom_message: message,
						reply_to_message_id: replyTo?.id,
					})
					trackWhatsAppMessageSent({ kind: "text", surface: "trip" })
				}
				setReplyTo(null)
			}}
			onSent={async () => {
				// Append just the sent message; refresh the overview for the
				// latest-message preview / thread ordering.
				await syncNew()
				await onRefresh()
			}}
			extraActions={
				<>
					<Button
						type="button"
						level="tertiary"
						inline
						onClick={openTemplateDialog}
						disabled={!threadPhoneNumberId}
						size="sm"
					>
						<Icons.DocumentText title="Use Template" />
					</Button>
					<Button
						type="button"
						level="tertiary"
						inline
						onClick={openQuickSendDialog}
						disabled={!threadPhoneNumberId}
						size="sm"
					>
						<Icons.LightningBoltSolid title="Quick Send" />
					</Button>
				</>
			}
		/>
	)

	return (
		<>
			<Stack>
				<WhatsAppConversationHeader
					thread={thread}
					actions={
						<WhatsAppFollowToggle
							thread={thread}
							onRefresh={onRefresh}
							iconOnly
						/>
					}
				/>
				<Box position="relative">
					<Box
						// Clicking into the conversation is engaging with it.
						onPointerDown={engage}
						style={
							isRevealed
								? undefined
								: {
										filter: "blur(5px)",
										// The messages behind the prompt are not
										// readable, so they are not reachable either.
										pointerEvents: "none",
										userSelect: "none",
									}
						}
						aria-hidden={!isRevealed}
					>
						<WhatsAppMessageList
							thread={thread}
							messages={messages}
							onRefresh={onRefresh}
							onLoadOlder={loadOlder}
							hasMoreOlder={hasMoreOlder}
							isLoadingOlder={isLoadingOlder}
							onReply={setReplyTo}
						/>
					</Box>
					{isRevealed ? null : (
						<Box
							position="absolute"
							top="0"
							right="0"
							bottom="0"
							left="0"
							display="flex"
							alignItems="center"
							justifyContent="center"
							padding="4"
						>
							<Stack gap="2" alignItems="center" textAlign="center">
								<Text fontWeight="semibold">
									{unreadCount === 1
										? "1 unread message"
										: `${unreadCount} unread messages`}
								</Text>
								<Box>
									<Button level="primary" size="sm" onClick={engage}>
										View messages
									</Button>
								</Box>
							</Stack>
						</Box>
					)}
				</Box>
				<Box
					borderTopWidth="1"
					paddingY="2"
					paddingX="4"
					// Replying is engaging with the conversation too.
					onPointerDown={engage}
					onFocusCapture={engage}
				>
					{composer}
				</Box>
			</Stack>

			<SendTripWhatsAppDialog
				open={isTemplateDialogOpen}
				onClose={closeTemplateDialog}
				tripId={tripId}
				overview={overview}
				threadId={thread.id}
				initialPhoneNumberId={threadPhoneNumberId}
				lockPhoneNumber
				templateOnly
				onSuccess={async () => {
					await onRefresh()
					closeTemplateDialog()
				}}
			/>
			<SendQuickSendDialog
				open={isQuickSendDialogOpen}
				onClose={closeQuickSendDialog}
				recipient={thread.contact?.name || thread.contact?.contact_number || ""}
				outsideServiceWindow={outsideServiceWindow}
				onSend={async ({ wa_quick_send_id }) => {
					const result = await xhr
						.post<TWAQuickSendResult>(`/trips/${tripId}/whatsapp`, {
							wa_phone_number_id: threadPhoneNumberId,
							// Answers whoever is in this conversation, which is
							// not always the trip's primary contact.
							wa_thread_id: thread.id,
							wa_quick_send_id,
						})
						.then((resp) => resp.data)
					trackWhatsAppMessageSent({ kind: "quick_send", surface: "trip" })
					return result
				}}
				onSent={async () => {
					await syncNew()
					await onRefresh()
				}}
			/>
		</>
	)
}

export type TSendTripWhatsAppAttachment = {
	tripQuoteId: number
	/** What the recipient will see, e.g. "Trip 1234 Quote.pdf". */
	fileName: string
}

type TSendTripWhatsAppValuesWithAttachment = TSendTripWhatsAppValues & {
	attach_document?: boolean
}

export function SendTripWhatsAppDialog({
	open,
	onClose,
	tripId,
	overview,
	threadId,
	initialPhoneNumberId,
	lockPhoneNumber = false,
	onSuccess,
	templateOnly,
	title = "Send WhatsApp Message",
	description,
	initialCustomMessage,
	parameterOverrides,
	attachment,
}: {
	open: boolean
	onClose: () => void
	tripId: number
	overview: TTripWhatsAppOverview
	/**
	 * The conversation this is sent into. Without it the message goes to the
	 * trip's primary contact, which is what the share flows want.
	 */
	threadId?: number
	initialPhoneNumberId?: number
	lockPhoneNumber?: boolean
	onSuccess: () => Promise<void>
	templateOnly?: boolean
	title?: string
	description?: string
	initialCustomMessage?: string
	/**
	 * Values to prefer over the server-resolved ones when prefilling template
	 * parameters — for a quote share, the quote actually being shared rather
	 * than the trip's latest.
	 */
	parameterOverrides?: Record<string, string>
	/**
	 * A stored document the sender may attach. Offered as a switch inside
	 * the dialog, defaulted on, so it does not depend on the caller having
	 * ticked something beforehand.
	 */
	attachment?: TSendTripWhatsAppAttachment
}) {
	const xhr = useXHR()
	const phoneNumbers = overview.phone_numbers
	const initialPhoneNumber = useMemo(
		() =>
			phoneNumbers.find(
				(phoneNumber) => phoneNumber.id === initialPhoneNumberId
			) || null,
		[initialPhoneNumberId, phoneNumbers]
	)
	// The seeded message and attachment are part of the identity: the share
	// dialog re-renders this with different content as inclusion toggles
	// change, and without them the form would keep its first values.
	const formKey = [
		String(initialPhoneNumberId || ""),
		lockPhoneNumber ? "locked" : "editable",
		phoneNumbers.map((phoneNumber) => phoneNumber.id).join(","),
		String(attachment?.tripQuoteId || ""),
		initialCustomMessage || "",
	].join(":")
	const validate = useCallback(
		(values: TSendTripWhatsAppValuesWithAttachment) => {
			const baseErrors = validateFormValues(validateSendTripWhatsApp)(values)

			if (
				!values.wa_template?.id &&
				!values.custom_message?.trim() &&
				!(attachment && values.attach_document)
			) {
				if (templateOnly) {
					return {
						...baseErrors,
						wa_template: "Please select a template to send.",
					}
				}
				return {
					...baseErrors,
					custom_message: "Please enter a custom message or choose a template.",
				}
			}

			return baseErrors
		},
		[templateOnly, attachment]
	)

	return (
		<Dialog open={open} onClose={onClose}>
			<Dialog.Header>
				<Dialog.Title>{title}</Dialog.Title>
			</Dialog.Header>
			<Form<TSendTripWhatsAppValuesWithAttachment>
				key={formKey}
				initialValues={{
					wa_template: undefined,
					wa_phone_number_id: initialPhoneNumber
						? String(initialPhoneNumber.id)
						: phoneNumbers.length === 1
							? String(phoneNumbers[0].id)
							: "",
					template_parameters: {},
					custom_message: initialCustomMessage || "",
					attach_document: Boolean(attachment),
				}}
				validate={validate}
				onSubmit={withServerErrors(async (values) => {
					await xhr.post(`/trips/${tripId}/whatsapp`, {
						wa_template_id: values.wa_template?.id,
						wa_thread_id: threadId,
						wa_phone_number_id: values.wa_phone_number_id
							? Number(values.wa_phone_number_id)
							: undefined,
						template_parameters: values.template_parameters,
						custom_message: values.custom_message?.trim() || undefined,
						trip_quote_id: values.attach_document
							? attachment?.tripQuoteId
							: undefined,
					})
					trackWhatsAppMessageSent({
						kind: values.wa_template?.id ? "template" : "text",
						surface: "trip",
					})
					showSnackbar("WhatsApp message sent successfully.")
					await onSuccess()
				})}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting, form }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Dialog.Body>
							<Stack gap="4">
								<Alert status="info">
									<Stack gap="2">
										<Text fontWeight="semibold">
											To: {overview.contact.name || "Customer"}
										</Text>
										<Text>{overview.contact.phone_number_plain}</Text>
										{description ? (
											<Text fontSize="sm">{description}</Text>
										) : null}
									</Stack>
								</Alert>
								<Stack gap="4">
									<SelectInputField
										onChange={(event) => {
											form.change(
												"wa_phone_number_id",
												event.currentTarget.value
											)
											form.change("wa_template", undefined)
											form.change("template_parameters", {})
										}}
										name="wa_phone_number_id"
										label="WhatsApp Phone Number"
										required
										disabled={lockPhoneNumber}
									>
										<option value="">Select WhatsApp phone number</option>
										{phoneNumbers.map((phoneNumber) => (
											<option
												key={phoneNumber.id}
												value={String(phoneNumber.id)}
											>
												{phoneNumber.name} · {phoneNumber.display_phone_number}
											</option>
										))}
									</SelectInputField>

									<GetFieldValue<string> name="wa_phone_number_id">
										{({ value: selectedPhoneNumberId }) => {
											const selectedPhoneNumber =
												phoneNumbers.find(
													(phoneNumber) =>
														String(phoneNumber.id) ===
														String(selectedPhoneNumberId || "")
												) || null

											return (
												<>
													{selectedPhoneNumber ? (
														<SelectField
															select={SelectWATemplate}
															name="wa_template"
															label="WhatsApp Template"
															secondaryLabel="optional"
															waPhoneNumberId={selectedPhoneNumber.id}
															sourceKey="trip"
															// A trip's own templates, plus any that
															// belong to no automation — the sender
															// fills those in by hand.
															includeSourceless
															fullWidth
															onChange={(option) => {
																form.change("wa_template", option)
																form.change("template_parameters", {})
															}}
														/>
													) : null}
												</>
											)
										}}
									</GetFieldValue>

									<GetFieldValue<TWATemplate | undefined> name="wa_template">
										{({ value: selectedTemplate }) => {
											const parameterNames =
												getTemplateParameterNames(selectedTemplate)

											return parameterNames.length ? (
												<Stack gap="3">
													<SyncTripWhatsAppTemplateParameters
														tripId={tripId}
														template={selectedTemplate}
														form={form}
														overrides={parameterOverrides}
													/>
													<Text fontWeight="semibold">Template Parameters</Text>
													{parameterNames.map((parameter) => (
														<TextInputField
															key={parameter}
															name={`template_parameters.${parameter}`}
															label={parameter}
															required
														/>
													))}
												</Stack>
											) : null
										}}
									</GetFieldValue>
								</Stack>
								{!templateOnly ? (
									<Stack gap="2">
										<TextAreaInputField
											name="custom_message"
											label="Custom Message"
											secondaryLabel="optional"
											rows={initialCustomMessage?.length ? 10 : 4}
											placeholder="Type a personal follow-up message..."
										/>
										{attachment ? (
											<GetFieldValue<boolean> name="attach_document">
												{({ value: attaching }: { value?: boolean }) =>
													attaching ? (
														<GetFieldValue<
															string | undefined
														> name="custom_message">
															{({ value }: { value?: string }) =>
																(value?.trim().length || 0) >
																WHATSAPP_CAPTION_MAX_LENGTH ? (
																	<Text fontSize="sm" color="muted">
																		<Icons.Info /> Long messages are sent as a
																		separate message after the document.
																	</Text>
																) : null
															}
														</GetFieldValue>
													) : null
												}
											</GetFieldValue>
										) : null}
									</Stack>
								) : null}
								{attachment ? (
									<SwitchInputField
										name="attach_document"
										label={`Attach ${attachment.fileName}`}
										disabled={submitting}
									/>
								) : null}
								<SubmissionError />
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Stack gap="4">
								<GetFieldValue<string> name="wa_phone_number_id">
									{({ value: selectedPhoneNumberId }) => {
										// The window is per conversation, so it follows the
										// sender: the thread is the one this number already has
										// with this customer. No thread yet means no inbound
										// message yet, which is outside the window too.
										const thread = overview.threads?.find(
											(t) =>
												String(t.phone_number?.id || "") ===
												String(selectedPhoneNumberId || "")
										)
										if (
											!isOutsideCustomerServiceWindow(
												thread?.last_inbound_message_at
											)
										) {
											return null
										}
										return (
											<Alert
												status="warning"
												title="Outside the 24 hour window"
											>
												<Text fontSize="sm">
													This customer has not messaged in the last{" "}
													{CUSTOMER_SERVICE_WINDOW_HOURS} hours, so WhatsApp
													will reject a plain message. Choose a template below
													to reopen the conversation.
												</Text>
											</Alert>
										)
									}}
								</GetFieldValue>
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Sending..." : "Send"}{" "}
										<Icons.PaperAirplane rotate="90" />
									</Button>
									<Button
										type="button"
										level="tertiary"
										onClick={onClose}
										disabled={submitting}
									>
										Cancel
									</Button>
								</Inline>
							</Stack>
						</Dialog.Footer>
					</form>
				)}
			</Form>
		</Dialog>
	)
}

function SyncTripWhatsAppTemplateParameters({
	tripId,
	template,
	form,
	overrides,
}: {
	tripId: number
	template?: TWATemplate
	form: FormApi<TSendTripWhatsAppValues>
	overrides?: Record<string, string>
}) {
	const templateId = template?.id
	const { data } = useFetchWATemplateParameters(template, tripId)

	useEffect(() => {
		if (!data?.length) {
			return
		}

		const currentParameters = form.getState().values.template_parameters || {}
		const nextParameters = { ...currentParameters }
		let hasChanges = false

		data.forEach((item) => {
			const currentValue = currentParameters[item.key]

			if (currentValue && currentValue.trim() !== "") {
				return
			}

			// An override wins over the server's value — it knows which record
			// is actually being shared. Fields the operator has typed into are
			// still left alone by the guard above.
			const nextValue = overrides?.[item.key] || item.value || ""

			if ((currentValue || "") === nextValue) {
				return
			}

			nextParameters[item.key] = nextValue
			hasChanges = true
		})

		if (hasChanges) {
			form.change("template_parameters", nextParameters)
		}
	}, [data, form, templateId, overrides])

	return null
}
